import React, {memo, useCallback, useMemo, useState} from "react"
import {SettingsPageContainer} from "../../../../components/Settings/SettingsPageContainer"
import {PasswordContainer} from "../../../../components/PasswordContainer"
import {useLocalization} from "../../../../hooks/localization"
import {SettingCheckBoxComponent} from "../../../../components/Settings/SettingCheckboxComponent"
import {useSelector} from "react-redux"
import {useRPC} from "../../../../hooks/rpc"
import {useMessage} from "../../../../hooks/message"
import {useQuestion} from "../../../../hooks/question"
import {SettingSelectComponent} from "../../../../components/Settings/SettingSelectComponent"
import {SelectItem} from "../../../../components/Select"
import {hasAuthority} from "../../../../utils/role"
import {UserRole} from "../../../../types/control"
import {useHistory} from "react-router-dom"

export const AdvancedPanelSettings = memo(function AdvancedPanelSettings() {
    const {t} = useLocalization()
    const mousePointerStatus = useSelector(state => state.control.mousePointerStatus)
    const externalUsbStatus = useSelector(state => state.control.externalUsbStatus)
    const {failure} = useMessage()
    const screenRotation = useSelector(state => state.control.workerConfigPanelSettings?.rotation)
    const question = useQuestion()
    const history = useHistory()
    const [rpc] = useRPC()
    const [mousePointer, setMousePointer] = useState(mousePointerStatus || false)
    const [externalUsb, setExternalUsb] = useState(externalUsbStatus || false)
    const role = useSelector(state => state.ui.role)
    const allowedRole = useCallback((select: UserRole) => hasAuthority(role, select), [role])

    const rotateTypeList = useMemo<SelectItem<string>[]>(
        () => [
            {
                value: "0",
                label: "0°"
            },
            {
                value: "90",
                label: "90°"
            },
            {
                value: "180",
                label: "180°"
            },
            {
                value: "270",
                label: "270°"
            }
        ],
        []
    )

    const [selectedRotate, setSelectedRotate] = useState<SelectItem<string>>(
        screenRotation == null
            ? rotateTypeList[0]
            : rotateTypeList.find(screenRotate => screenRotate.value === screenRotation)
    )

    const onChangeMousePointer = useCallback(() => setMousePointer(p => !p), [])
    const onChangeExternalUsb = useCallback(() => setExternalUsb(p => !p), [])

    const onClickSave = useCallback(() => {
        return question(
            t("the_window_will_reload"),
            //async () =>
            //await Promise.all([
            //    rpc.updateRotateType(selectedRotate?.value),
            //    rpc.updateMouseCursorVisibility(mousePointer),
            //    rpc.updateUsbConnectionStatus(externalUsb)
            //])
            async () => {
                const promises = [];
                
                if (selectedRotate?.value !== screenRotation) {
                    promises.push(rpc.updateRotateType(selectedRotate?.value));
                }
                
                if (mousePointer !== mousePointerStatus) {
                    promises.push(rpc.updateMouseCursorVisibility(mousePointer));
                }
                
                if (externalUsb !== externalUsbStatus) {
                    promises.push(rpc.updateUsbConnectionStatus(externalUsb));
                }
                
                return await Promise.all(promises)  //
                    .then(() => {
                        history.push("/settings/advanced-settings")
                        window.location.reload()
                    })
                    .catch(failure)
            }
        )
    }, [failure, question, t, rpc, selectedRotate, mousePointer, externalUsb, history, screenRotation, mousePointerStatus, externalUsbStatus])

    const buttons = useMemo(
        () => [
            {
                text: t("save"),
                className: "green",
                onClick: onClickSave
            }
        ],
        [t, onClickSave]
    )

    return (
        <PasswordContainer requiredRole="service" isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                className="service-edit"
                backUrl={"/settings/advanced-settings"}
                style={null}
            >
                <div className="content-divider">
                    <div className="content-wrapper">
                        {renderIf(allowedRole("developer"))(() => (
                            <>
                                <SettingCheckBoxComponent
                                    label={t("mouse_pointer")}
                                    set={onChangeMousePointer}
                                    value={mousePointer}
                                />
                                <SettingCheckBoxComponent
                                    label={t("external_usb")}
                                    set={onChangeExternalUsb}
                                    value={externalUsb}
                                />
                            </>
                        ))}
                        {renderIf(allowedRole("service"))(() => (
                            <>
                                <SettingSelectComponent
                                    label={t("customizations.screen_direction")}
                                    options={rotateTypeList}
                                    value={selectedRotate}
                                    onChange={setSelectedRotate}
                                />
                            </>
                        ))}
                    </div>
                    <div className="content-wrapper"></div>
                </div>
            </SettingsPageContainer>
        </PasswordContainer>
    )
})
